// JSON-LD schema builders for SEO + GEO/LLM visibility.
// All builders return plain JS objects to be passed to <SeoHead jsonLd={...}>.
import { htmlToPlain } from "@/lib/text";

const SITE_NAME = "Dataleo Supply Chain AI Radar";

// Strip HTML tags + decode HTML entities for clean description fields
// (e.g. "S&amp;OP" → "S&OP", "<p>foo</p>" → "foo").
const stripHtml = (html) => htmlToPlain(html);

// Truncate to a target length without breaking words.
export const clampText = (text, max = 160) => {
  const clean = stripHtml(text);
  if (clean.length <= max) return clean;
  const cut = clean.slice(0, max - 1);
  return cut.slice(0, cut.lastIndexOf(" ")) + "\u2026";
};

/**
 * Normalise a date value to an ISO 8601 string suitable for
 * schema.org `datePosted` / `datePublished`. Handles:
 *   - already ISO datetime (`2026-05-29T10:00:00Z`) → returned as-is
 *   - date-only (`2026-05-29`) → returned as-is (valid ISO 8601)
 *   - empty / null → falls back to `fallback` (usually `created_at`)
 *   - anything else parseable → normalised to `YYYY-MM-DD`
 *
 * Returns `undefined` when no usable value could be resolved so
 * `JSON.stringify` drops the key entirely instead of emitting `""`
 * (which Google flags as "missing" for JobPosting.datePosted).
 */
export const normalizeIsoDate = (value, fallback) => {
  const pick = (v) => (typeof v === "string" ? v.trim() : "");
  const primary = pick(value) || pick(fallback);
  if (!primary) return undefined;
  // Already ISO date or datetime.
  if (/^\d{4}-\d{2}-\d{2}/.test(primary)) return primary;
  // Try to coerce anything else via Date.
  const d = new Date(primary);
  if (Number.isNaN(d.getTime())) return undefined;
  return d.toISOString().slice(0, 10);
};

export const buildOrganizationSchema = ({ baseUrl, settings = {} }) => {
  const sameAs = [
    settings.linkedin_dataleo_url,
    settings.linkedin_jfn_url,
    settings.substack_url,
  ].filter(Boolean);
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "Dataleo",
    alternateName: SITE_NAME,
    url: baseUrl,
    logo: `${baseUrl}/logo512.png`,
    description:
      "Dataleo helps companies build, govern and industrialize AI-built decision tools in Supply Chain and Operations.",
    sameAs,
    contactPoint: settings.phone
      ? [{
          "@type": "ContactPoint",
          telephone: settings.phone,
          contactType: "customer support",
          email: settings.contact_email || "contact@dataleo.com",
          areaServed: ["FR", "EN"],
          availableLanguage: ["French", "English"],
        }]
      : undefined,
  };
};

export const buildWebsiteSchema = ({ baseUrl }) => ({
  "@context": "https://schema.org",
  "@type": "WebSite",
  name: SITE_NAME,
  url: baseUrl,
  inLanguage: ["en", "fr"],
  potentialAction: {
    "@type": "SearchAction",
    target: `${baseUrl}/search?q={search_term_string}`,
    "query-input": "required name=search_term_string",
  },
});

export const buildLocalBusinessSchema = ({ baseUrl, settings = {} }) => ({
  "@context": "https://schema.org",
  "@type": "LocalBusiness",
  name: "Dataleo",
  url: baseUrl,
  telephone: settings.phone || undefined,
  email: settings.contact_email || undefined,
  address: settings.address_street
    ? {
        "@type": "PostalAddress",
        streetAddress: settings.address_street,
        addressLocality: settings.address_city || undefined,
        postalCode: settings.address_postal_code || undefined,
        addressCountry: settings.address_country || undefined,
      }
    : undefined,
});

export const buildPersonSchema = ({ baseUrl, settings = {} }) => ({
  "@context": "https://schema.org",
  "@type": "Person",
  name: "Jean-François Nordmann",
  jobTitle: "Founder, Dataleo",
  worksFor: { "@type": "Organization", name: "Dataleo", url: baseUrl },
  sameAs: [settings.linkedin_jfn_url].filter(Boolean),
});

export const buildArticleSchema = ({ baseUrl, item, type = "NewsArticle", path }) => {
  const url = `${baseUrl}${path}`;
  const desc = clampText(item.summary || item.subtitle || item.dataleo_angle, 200);
  return {
    "@context": "https://schema.org",
    "@type": type,
    mainEntityOfPage: { "@type": "WebPage", "@id": url },
    headline: item.title,
    description: desc,
    image: item.image_url || item.featured_image || undefined,
    datePublished: item.published_at || item.created_at,
    dateModified: item.updated_at || item.published_at,
    inLanguage: item.language || "en",
    author: {
      "@type": "Organization",
      name: item.source || "Dataleo",
    },
    publisher: {
      "@type": "Organization",
      name: "Dataleo",
      logo: { "@type": "ImageObject", url: `${baseUrl}/logo512.png` },
    },
    keywords: (item.tags || []).join(", "),
    articleSection: item.relevance_area || item.category,
    url,
  };
};

export const buildHowToSchema = ({ baseUrl, item, path }) => ({
  "@context": "https://schema.org",
  "@type": "HowTo",
  name: item.title,
  description: clampText(item.subtitle || item.body_html, 200),
  image: item.featured_image,
  totalTime: item.duration_minutes ? `PT${item.duration_minutes}M` : undefined,
  inLanguage: item.language || "en",
  url: `${baseUrl}${path}`,
  publisher: {
    "@type": "Organization",
    name: "Dataleo",
    logo: { "@type": "ImageObject", url: `${baseUrl}/logo512.png` },
  },
});

const EMPLOYMENT_TYPES = {
  "Full-time": "FULL_TIME", "Part-time": "PART_TIME", Freelance: "CONTRACTOR", Mission: "CONTRACTOR",
  "Fixed-term": "TEMPORARY", Internship: "INTERN", Apprenticeship: "INTERN",
};

const employmentTypes = (item) => {
  const out = [...new Set([EMPLOYMENT_TYPES[item.time_type], EMPLOYMENT_TYPES[item.contract_type]].filter(Boolean))];
  return out.length ? out : undefined;
};

export const buildJobPostingSchema = ({ baseUrl, item, path }) => {
  // Google's Rich Results test flags a MISSING `datePosted` when the value is
  // an empty string. Fall back to `created_at` and drop the key entirely
  // (via undefined) if nothing usable resolves.
  const datePosted = normalizeIsoDate(item.published_at, item.created_at);
  // `validThrough` isn't required but Google recommends it; default to
  // datePosted + 90 days when we have a valid datePosted.
  let validThrough;
  if (datePosted) {
    const d = new Date(`${datePosted.slice(0, 10)}T00:00:00Z`);
    if (!Number.isNaN(d.getTime())) {
      d.setUTCDate(d.getUTCDate() + 90);
      validThrough = d.toISOString().slice(0, 10);
    }
  }
  return {
    "@context": "https://schema.org",
    "@type": "JobPosting",
    title: item.title,
    description: stripHtml(item.summary || item.dataleo_angle) || item.title,
    datePosted,
    validThrough,
    employmentType: employmentTypes(item),
    hiringOrganization: {
      "@type": "Organization",
      name: item.company || "Dataleo Radar",
      logo: item.company_logo || undefined,
    },
    jobLocation: item.location
      ? {
          "@type": "Place",
          address: {
            "@type": "PostalAddress",
            addressLocality: item.location,
            addressCountry: item.country || undefined,
          },
        }
      : undefined,
    jobLocationType: item.work_mode === "Remote" ? "TELECOMMUTE" : undefined,
    applicantLocationRequirements: item.country
      ? { "@type": "Country", name: item.country }
      : undefined,
    experienceRequirements: item.seniority || undefined,
    url: `${baseUrl}${path}`,
    directApply: false,
  };
};

export const buildEcosystemOrgSchema = ({ baseUrl, entity, path }) => ({
  "@context": "https://schema.org",
  "@type": "Organization",
  name: entity.name,
  url: entity.website || `${baseUrl}${path}`,
  logo: entity.logo_url || undefined,
  description: clampText(entity.description, 300),
  sameAs: [entity.linkedin, entity.website].filter(Boolean),
});

export const buildContactPageSchema = ({ baseUrl, settings = {} }) => ({
  "@context": "https://schema.org",
  "@type": "ContactPage",
  url: `${baseUrl}/contact`,
  name: "Contact Dataleo",
  mainEntity: {
    "@type": "Organization",
    name: "Dataleo",
    email: settings.contact_email || undefined,
    telephone: settings.phone || undefined,
    address: settings.address_street
      ? {
          "@type": "PostalAddress",
          streetAddress: settings.address_street,
          addressLocality: settings.address_city || undefined,
          postalCode: settings.address_postal_code || undefined,
          addressCountry: settings.address_country || undefined,
        }
      : undefined,
  },
});

export const buildBreadcrumbSchema = ({ baseUrl, items }) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: items.map((it, i) => ({
    "@type": "ListItem",
    position: i + 1,
    name: it.name,
    item: `${baseUrl}${it.path}`,
  })),
});
